import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    IconButton,
    Typography,
    Box,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { searchHostlyUsers, HostlyUser } from '../../services/userSearchService';
import { sharePost } from '../../services/notificationsService';

interface SharePostDialogProps {
    open: boolean;
    onClose: () => void;
    postId: string;
}

export const SharePostDialog: React.FC<SharePostDialogProps> = ({
    open,
    onClose,
    postId,
}) => {
    const { theme } = useTheme();
    const { user, hostlyUser } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<HostlyUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [sentMap, setSentMap] = useState<Record<string, boolean>>({});

    const handleSearch = async (term: string) => {
        setSearchTerm(term);
        if (term.length < 2) {
            setSearchResults([]);
            return;
        }

        setLoading(true);
        try {
            const results = await searchHostlyUsers(term);
            // Remove o próprio usuário da lista
            const filteredResults = results.filter((u) => u.id !== user?.uid);
            setSearchResults(filteredResults);
        } catch (error) {
            console.error('Erro na busca:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async (recipient: HostlyUser) => {
        if (!user?.uid || !hostlyUser?.name) return;

        try {
            const success = await sharePost(
                postId,
                user.uid,
                hostlyUser.name,
                recipient.id
            );

            if (success) {
                setSentMap((prev) => ({ ...prev, [recipient.id]: true }));
            }
        } catch (error) {
            console.error('Erro ao enviar:', error);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: theme.radii.medium,
                    backgroundColor: theme.colors.background,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: theme.colors.text.primary,
                }}
            >
                Compartilhar
                <IconButton onClick={onClose} sx={{ color: theme.colors.text.secondary }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {/* Search Input */}
                <TextField
                    fullWidth
                    placeholder="Buscar pessoas..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: theme.colors.text.secondary }} />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: theme.radii.pill,
                            backgroundColor: theme.colors.card,
                            '& fieldset': { border: 'none' },
                        },
                    }}
                />

                {/* Results List */}
                <Box sx={{ minHeight: 200, maxHeight: 300, overflowY: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <CircularProgress size={24} sx={{ color: theme.colors.primary }} />
                        </Box>
                    ) : searchResults.length > 0 ? (
                        <List>
                            {searchResults.map((recipient) => (
                                <ListItem
                                    key={recipient.id}
                                    secondaryAction={
                                        <Button
                                            variant={sentMap[recipient.id] ? 'outlined' : 'contained'}
                                            disabled={sentMap[recipient.id]}
                                            onClick={() => handleSend(recipient)}
                                            sx={{
                                                borderRadius: theme.radii.pill,
                                                textTransform: 'none',
                                                minWidth: 80,
                                                backgroundColor: sentMap[recipient.id]
                                                    ? 'transparent'
                                                    : theme.colors.primary,
                                                color: sentMap[recipient.id]
                                                    ? theme.colors.text.secondary
                                                    : '#fff',
                                                borderColor: sentMap[recipient.id]
                                                    ? theme.colors.border
                                                    : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: sentMap[recipient.id]
                                                        ? 'transparent'
                                                        : theme.colors.primary,
                                                    opacity: 0.9,
                                                },
                                            }}
                                        >
                                            {sentMap[recipient.id] ? 'Enviado' : 'Enviar'}
                                        </Button>
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            src={recipient.profilePictureUrl}
                                            sx={{ backgroundColor: theme.colors.primary }}
                                        >
                                            {recipient.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="body1"
                                                sx={{ color: theme.colors.text.primary, fontWeight: 500 }}
                                            >
                                                {recipient.name}
                                            </Typography>
                                        }
                                        secondary={
                                            recipient.username && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: theme.colors.text.secondary }}
                                                >
                                                    @{recipient.username}
                                                </Typography>
                                            )
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : searchTerm ? (
                        <Typography
                            align="center"
                            sx={{ color: theme.colors.text.secondary, mt: 4 }}
                        >
                            Nenhum usuário encontrado.
                        </Typography>
                    ) : (
                        <Typography
                            align="center"
                            sx={{ color: theme.colors.text.secondary, mt: 4 }}
                        >
                            Digite para buscar amigos.
                        </Typography>
                    )}
                </Box>

            </DialogContent>
        </Dialog>
    );
};
